import {
  HOSPITOL_BRAND,
  HOSPITOL_EDITION_ORDER,
  HOSPITOL_EDITIONS,
  HOSPITOL_INCLUDED_SERVICES,
  HOSPITOL_PREMIUM_ADDONS,
  type HospitolEditionId,
} from "@itaas/hospitol-editions";
import type { ProductPromotion } from "@itaas/pep-contracts";
import { ProductPromotionBadge } from "../../components/commercial/ProductPromotionBadge";
import { PromoPriceUsd } from "../../components/commercial/PromoPrice";
import {
  discountedAmount,
  isPromotionActive,
  resolveActivePromotion,
} from "../../utils/product-promotion";

interface TrialHospitolEditionStepProps {
  value: HospitolEditionId;
  onChange: (edition: HospitolEditionId) => void;
  promotion?: ProductPromotion | null;
}

export function TrialHospitolEditionStep({
  value,
  onChange,
  promotion,
}: TrialHospitolEditionStepProps) {
  return (
    <div className="mb-4">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h2 className="h6 fw-bold mb-0">Choisissez l&apos;édition Hospitol</h2>
        {isPromotionActive(promotion) ? (
          <ProductPromotionBadge promotion={promotion} locale="fr" />
        ) : null}
      </div>
      <p className="text-secondary small mb-1">{HOSPITOL_BRAND.signatureFr}</p>
      <p className="text-secondary small mb-3">
        Chaque édition inclut les pôles de la précédente. Les modules premium (applications
        patient, SMS, BI…) sont disponibles en option sur toutes les éditions.
      </p>
      <div className="row g-3">
        {HOSPITOL_EDITION_ORDER.map((id) => {
          const ed = HOSPITOL_EDITIONS[id];
          const activePromo = resolveActivePromotion(promotion, id);
          const priceContent =
            ed.monthlyUsd === null ? (
              <span className="pep-edition-card__price">Sur devis</span>
            ) : activePromo ? (
              <span className="pep-edition-card__price">
                <span className="d-block small text-secondary mb-1">
                  {id === "full" ? "À partir de" : ""}
                </span>
                <PromoPriceUsd
                  originalUsd={ed.monthlyUsd}
                  promoUsd={discountedAmount(ed.monthlyUsd, activePromo.discountPercent)}
                />
                <span className="d-block small text-secondary mt-1">USD/mois</span>
              </span>
            ) : (
              <span className="pep-edition-card__price">
                {id === "full" ? "À partir de " : ""}
                {ed.monthlyUsd} USD/mois
              </span>
            );
          return (
            <div className="col-md-6 col-xl-4" key={id}>
              <button
                type="button"
                className={`pep-edition-card w-100 text-start h-100${value === id ? " is-selected" : ""}${ed.highlight ? " is-recommended" : ""}`}
                onClick={() => onChange(id)}
              >
                {ed.highlight ? <span className="pep-edition-card__badge">Populaire</span> : null}
                {activePromo ? (
                  <span className="pep-edition-card__badge pep-edition-card__badge--promo">
                    {activePromo.badgeFr ?? `−${activePromo.discountPercent} %`}
                  </span>
                ) : null}
                <span className="pep-edition-card__name">{ed.displayName.replace("Hospitol ", "")}</span>
                <span className="pep-edition-card__tagline">{ed.tagline}</span>
                {priceContent}
                <span className="pep-edition-card__capacity">{ed.maxUsersLabel}</span>
                <ul className="pep-edition-card__modules">
                  {(ed.newInEdition ?? ed.poles).slice(0, 6).map((pole) => (
                    <li key={pole}>{pole}</li>
                  ))}
                  {(ed.newInEdition ?? ed.poles).length > 6 ? (
                    <li className="text-secondary">+ autres pôles</li>
                  ) : null}
                </ul>
                <span className="d-block small text-secondary mt-2">
                  Idéal pour : {ed.idealFor.slice(0, 2).join(", ")}
                  {ed.idealFor.length > 2 ? "…" : ""}
                </span>
              </button>
            </div>
          );
        })}
      </div>
      <div className="row g-3 mt-2">
        <div className="col-md-7">
          <div className="border rounded-3 p-3 h-100 bg-body-tertiary">
            <h3 className="h6 fw-semibold mb-2">Inclus dans toutes les éditions</h3>
            <ul className="small mb-0 ps-3">
              {HOSPITOL_INCLUDED_SERVICES.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="col-md-5">
          <div className="border rounded-3 p-3 h-100">
            <h3 className="h6 fw-semibold mb-2">Modules premium (option)</h3>
            <ul className="small mb-0 ps-3 text-secondary">
              {HOSPITOL_PREMIUM_ADDONS.map((addon) => (
                <li key={addon}>{addon}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
